import { v4 } from 'uuid';
import { createApiInstance } from './handler';
import { getProductTargets, getUser } from './users-api';
import {
  processExperienceResult,
  processPersonalityResult,
  processRaterCompetenciesResult,
  processSelfCompetenciesResult,
  processSituationalJudgmentResult,
  processValuesResult,
} from './assessment-calculations';
import { DateTime } from 'luxon';
import { getClientConfiguration, getClientOrgValues } from './client';
import { PRODUCT_CLASSIFICATION } from '../utils/constants';
import { getGlobalValue, setGlobalValue } from '../utils/global-storage';
import { buildStoredFilters } from './metadata';
import { getOpeningType, LEADERSHIP_OPENING } from '../components/shared/filters/filter-common';

const STORED_USER_METRICS = 'hutrics.user.metrics';

const getStoredMetrics = () => {
  return getGlobalValue(STORED_USER_METRICS);
};
const storeMetrics = (userMetrics) => {
  setGlobalValue(STORED_USER_METRICS, userMetrics);
};
const reviewPercentiles = (percentiles) => {
  if (!percentiles) {
    return undefined;
  }

  const updatedPercentiles = {};

  for (let [key, value] of Object.entries(percentiles)) {
    let updatedValue = value;
    if (value < 1) {
      updatedValue = 1;
    } else if (value > 100) {
      updatedValue = 100;
    }

    updatedPercentiles[key] = updatedValue;
  }

  return updatedPercentiles;
};

export const isSelectStaffing = () => {
  const configuration = getClientConfiguration();
  return configuration?.select?.features?.staffing;
};

export const isStaffingCandidate = (candidate) => {
  const percentiles = candidate.percentiles;
  return percentiles[getPercentileAttributesConstants().onsite.performance] !== undefined;
};

export const getPercentileAttributesConstants = () => {
  return {
    default: {
      performance: 'performance',
      loyalty: 'loyalty',
      personality: 'personality',
      values: 'values',
      judgment: 'judgment',
      experience: 'experience',
    },
    onsite: {
      performance: 'onsitePerformance',
      loyalty: 'onsiteLoyalty',
      personality: 'onsitePersonality',
      values: 'onsiteValues',
      judgment: 'onsiteJudgment',
      experience: 'onsiteExperience',
    },
    remote: {
      performance: 'remotePerformance',
      loyalty: 'remoteLoyalty',
      personality: 'remotePersonality',
      values: 'remoteValues',
      judgment: 'remoteJudgment',
      experience: 'remoteExperience',
    },
  };
};

export const getPercentileAttributes = () => {
  const openingType = getOpeningType();
  if (isSelectStaffing() && openingType !== LEADERSHIP_OPENING) {
    return {
      performance: `${getOpeningType()}Performance`,
      loyalty: `${getOpeningType()}Loyalty`,
      personality: `${getOpeningType()}Personality`,
      values: `${getOpeningType()}Values`,
      judgment: `${getOpeningType()}Judgment`,
      experience: `${getOpeningType()}Experience`,
    };
  } else {
    return {
      performance: 'performance',
      loyalty: 'loyalty',
      acumen: 'acumen',
      personality: 'personality',
      values: 'values',
      judgment: 'judgment',
      experience: 'experience',
    };
  }
};

export const getUserAggregation = async (auth) => {
  const storedMetrics = getStoredMetrics();
  if (storedMetrics) {
    return Promise.resolve(storedMetrics);
  }

  const users = await getProductTargets(auth);
  await buildStoredFilters(auth, users);
  const productClassification = auth.isDevelop() ? PRODUCT_CLASSIFICATION.develop : PRODUCT_CLASSIFICATION.select;

  if (users && users.length) {
    try {
      const userIds = [];
      users.forEach((user) => {
        userIds.push(user.id);
      });
      let strUserIds = userIds.join(',');
      const request = {
        target_user_ids: `${strUserIds}`,
        only_create_if_does_not_exist: true,
        product_classification: productClassification,
      };
      const processedIds = await createApiInstance(auth, 'client').post('/leadership-model-processings', request);
      if (processedIds.data?.length > 0) {
        const idsWithResults = [];
        processedIds.data.forEach((result) => {
          if (result.id) {
            idsWithResults.push(+result.target_user_id);
          }
        });
        if (idsWithResults.length === 0) {
          return [];
        }
        strUserIds = idsWithResults.join(',');
      } else {
        return [];
      }
      const results = await createApiInstance(auth, 'client').get(
        `/user-specific-assessment-result-details?limit=1000&user_ids=${strUserIds}&product_classification=${productClassification}`
      );
      if (results.data?.data) {
        const orgValues = getClientOrgValues();
        const userMetrics = [];
        results.data.data.forEach((result) => {
          if (result.leadership_model_processings[0]?.assessment_assignments[0]) {
            for (const element of users) {
              if (element.id === result.user_id) {
                const user = element;
                const submittedTime = DateTime.fromISO(
                  result.leadership_model_processings[0].assessment_assignments[0].submitted
                ).toLocal();
                const submitted = submittedTime.toLocaleString(DateTime.DATE_FULL);
                const raterCompetencies = processRaterCompetenciesResult(result);
                userMetrics.push({
                  userId: user.id,
                  name: user.name,
                  reference: v4(),
                  submitted,
                  percentiles: reviewPercentiles(result.leadership_model_processings[0]?.percentiles),
                  raters: result.leadership_model_processings[0]?.raters,
                  personality: processPersonalityResult(result),
                  situationalJudgment: processSituationalJudgmentResult(result),
                  experience: processExperienceResult(result),
                  values: processValuesResult(result, orgValues),
                  raterCompetencies: raterCompetencies,
                  selfCompetencies: processSelfCompetenciesResult(result, raterCompetencies),
                  metadata: user.metadata,
                });
                break;
              }
            }
          }
        });
        storeMetrics(userMetrics);
        return userMetrics;
      }
    } catch (reason) {
      console.log(reason); // TODO Some real logging
    }
  }

  /**
   TODO: The internally conducted interview process does not work. It needs to be corrected..
   if (publishedInterviews.includes(candidate.userId)) {
   const dimensionScores = await getCandidateInterviewAssessmentScores(auth, candidate.userId);
   if (dimensionScores) {
   candidate.interviewCompetencies = generateInterviewCompetenciesResult(dimensionScores);
   }
   }
   */
  /** TODO Do this in bulk. Multiple calls is a performance issue
   const dimensionScores = await getCandidateInterviewAssessmentScores(auth, candidate.userId);
   if (dimensionScores) {
   candidate.interviewCompetencies = generateInterviewCompetenciesResult(dimensionScores);
   }
   */
  return Promise.resolve([]);
};

export const getScorecardMetrics = async (auth) => {
  const userMetrics = await getUserAggregation(auth);
  return {
    candidates: userMetrics,
  };
};

export const getUserByReference = async (auth, reference) => {
  const storedMetrics = getStoredMetrics();
  if (storedMetrics) {
    for (const element of storedMetrics) {
      if (element.reference === reference) {
        return getUserMetrics(auth, element.userId);
      }
    }
  }

  return Promise.resolve({});
};

export const getUserMetricsByReference = async (auth, reference) => {
  const storedMetrics = getStoredMetrics();
  if (storedMetrics) {
    for (const element of storedMetrics) {
      if (element.reference === reference) {
        return getUserMetrics(auth, element.userId);
      }
    }
  }

  return Promise.resolve({});
};

export const getUserMetrics = async (auth, userId, refresh) => {
  const refreshMetrics = auth.isDevelop() ? true : refresh;
  let userReference;
  const storedMetrics = getStoredMetrics();
  if (storedMetrics) {
    for (const element of storedMetrics) {
      if (element.userId === userId) {
        if (!refreshMetrics) {
          return Promise.resolve(element);
        } else {
          userReference = element.reference;
          break;
        }
      }
    }
  }

  const productClassification = auth.isDevelop() ? PRODUCT_CLASSIFICATION.develop : PRODUCT_CLASSIFICATION.select;
  const user = await getUser(auth, userId);

  const request = {
    target_user_ids: `${userId}`,
    only_create_if_does_not_exist: true,
    product_classification: productClassification,
    update_raters: !!auth.isDevelop(),
  };
  let userHasResults = false;
  const processedIds = await createApiInstance(auth, 'client').post('/leadership-model-processings', request);
  if (processedIds.data?.length > 0) {
    for (const result of processedIds.data) {
      if (+result.target_user_id === userId) {
        if (result.id) {
          userHasResults = true;
          break;
        }
      }
    }
    if (!userHasResults) {
      return getUser(auth, userId);
    }
  }
  const results = await createApiInstance(auth, 'client').get(
    `/user-specific-assessment-result-details?user_ids=${userId}&product_classification=${productClassification}`
  );
  const result = results.data?.data?.length > 0 ? results.data.data[0] : undefined;
  if (result && result.leadership_model_processings?.length > 0) {
    const orgValues = getClientOrgValues();
    const submittedTime = DateTime.fromISO(
      result.leadership_model_processings[0].assessment_assignments[0].submitted
    ).toLocal();
    const submitted = submittedTime.toLocaleString(DateTime.DATE_FULL);
    const raterCompetencies = processRaterCompetenciesResult(result);
    return {
      userId: userId,
      name: user.name,
      reference: userReference ? userReference : v4(),
      submitted,
      percentiles: reviewPercentiles(result.leadership_model_processings[0]?.percentiles),
      raters: result.leadership_model_processings[0]?.raters,
      personality: processPersonalityResult(result),
      situationalJudgment: processSituationalJudgmentResult(result),
      experience: processExperienceResult(result),
      values: processValuesResult(result, orgValues),
      raterCompetencies: raterCompetencies,
      selfCompetencies: processSelfCompetenciesResult(result, raterCompetencies),
      metadata: user.metadata,
    };
  } else {
    return getUser(auth, userId);
  }
};

export const getUserMetricsByToken = async (auth, userId) => {
  return getUserMetrics(auth, userId || auth.userId, true);
};
